import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { ref, get } from 'firebase/database';
import './Guestbook.css';
import photoboothIcon from '../img/photobooth.png';
import gardenIcon from '../img/garden.png';

const API_URL = process.env.REACT_APP_API_URL;

interface Comment {
  id: string;
  handle: string;
  userId: string;
  message: string;
  photoUrl?: string | null;
  gardenPageId?: string | null;
  timestamp: number;
}

const COMMENTS_PER_PAGE = 50;
const MAX_CHAR_LIMIT = 280;

// Neon colors for user handles - readable against white background
const NEON_COLORS = [
  '#FF25F8', // Hot pink
  '#00B8D4', // Darker cyan
  '#00C853', // Darker green
  '#FF073A', // Neon red
  '#F57C00', // Dark orange
  '#AA00FF', // Purple
  '#7C4DFF', // Violet
  '#00897B', // Teal
];

// Simple hash function to consistently assign color to a handle
const getColorForHandle = (handle: string): string => {
  let hash = 0;
  for (let i = 0; i < handle.length; i++) {
    hash = handle.charCodeAt(i) + ((hash << 5) - hash);
  }
  return NEON_COLORS[Math.abs(hash) % NEON_COLORS.length];
};

interface GuestbookProps {
  onOpenImage?: (imageUrl: string, title: string) => void;
  onOpenGarden?: (gardenPageId: string, userHandle: string) => void;
  onOpenLoginSignup?: () => void;
  onOpenPhotoBooth?: () => void;
}

const Guestbook: React.FC<GuestbookProps> = ({ onOpenImage, onOpenGarden, onOpenLoginSignup, onOpenPhotoBooth }) => {
  const { currentUser, userHandle } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [displayCount, setDisplayCount] = useState(COMMENTS_PER_PAGE);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [photoboothPhotos, setPhotoboothPhotos] = useState<any[]>([]);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  const [selectedGardenPageId, setSelectedGardenPageId] = useState<string | null>(null);
  const [userGardenPageId, setUserGardenPageId] = useState<string | null>(null);
  const [showGardenSelector, setShowGardenSelector] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadComments();
    loadPhotoboothPhotos();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setShowLoginPrompt(true);
      setUserGardenPageId(null);
    } else {
      setShowLoginPrompt(false);
      checkUserGardenPage();
    }
  }, [currentUser]);

  const checkUserGardenPage = async () => {
    if (!currentUser) return;

    try {
      const pagesRef = ref(db, `userCollection/${currentUser.uid}/pages`);
      const snapshot = await get(pagesRef);

      if (snapshot.exists()) {
        const pages = snapshot.val();
        // Find the inorbit-online page
        const gardenPageId = Object.keys(pages).find(pageId =>
          pageId.startsWith('inorbit-online_')
        );

        if (gardenPageId) {
          console.log('📗 orbitBook - Found user garden page:', gardenPageId);
          setUserGardenPageId(gardenPageId);
        }
      }
    } catch (error) {
      console.error('Error checking user garden page:', error);
    }
  };

  const loadPhotoboothPhotos = () => {
    try {
      const saved = localStorage.getItem('photoboothPhotos');
      if (saved) {
        setPhotoboothPhotos(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading photobooth photos:', error);
    }
  };

  const loadComments = async () => {
    try {
      const response = await fetch(`${API_URL}/orbit/guestbook`);

      if (response.ok) {
        const data: Comment[] = await response.json();
        setComments(data);
      } else {
        console.error('Failed to load comments:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (dataUrl: string): Promise<string | null> => {
    try {
      console.log('📸 Starting photo upload...');
      console.log('📸 Data URL length:', dataUrl.length);
      console.log('📸 Current user ID:', currentUser?.uid);

      // Send base64 directly as JSON - much simpler and more reliable!
      const uploadResponse = await fetch(`${API_URL}/orbit/uploadPhoto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: dataUrl,
          userId: currentUser?.uid,
        }),
      });

      console.log('📸 Upload response status:', uploadResponse.status);

      if (uploadResponse.ok) {
        const photoUrl = await uploadResponse.text();
        console.log('📸 Photo uploaded successfully:', photoUrl);
        return photoUrl;
      } else {
        const errorText = await uploadResponse.text();
        console.error('❌ Failed to upload photo:', uploadResponse.statusText);
        console.error('❌ Error response:', errorText);
        return null;
      }
    } catch (error) {
      console.error('❌ Error uploading photo:', error);
      return null;
    }
  };

  const handlePost = async () => {
    if (!currentUser || !userHandle) return;
    if (!message.trim()) return;
    if (message.length > MAX_CHAR_LIMIT) return;

    setPosting(true);

    try {
      let photoUrl: string | null = null;

      // Upload photo if one is selected
      if (selectedPhoto) {
        photoUrl = await uploadPhoto(selectedPhoto);
        if (!photoUrl) {
          console.error('Photo upload failed, posting without photo');
        }
      }

      const response = await fetch(`${API_URL}/orbit/guestbook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          handle: userHandle,
          userId: currentUser.uid,
          message: message.trim(),
          photoUrl: photoUrl,
          gardenPageId: selectedGardenPageId,
        }),
      });

      if (response.ok) {
        const newComment: Comment = await response.json();
        // Add to local state
        setComments([newComment, ...comments]);
        setMessage('');
        setSelectedPhoto(null);
        setSelectedGardenPageId(null);
      } else {
        console.error('Failed to post comment:', response.statusText);
      }
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteClick = (commentId: string) => {
    setCommentToDelete(commentId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!commentToDelete || !currentUser) return;

    try {
      const response = await fetch(`${API_URL}/orbit/guestbook/${commentToDelete}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.uid,
        }),
      });

      if (response.ok) {
        // Remove from local state
        setComments(comments.filter(c => c.id !== commentToDelete));
      } else {
        console.error('Failed to delete comment:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setShowDeleteModal(false);
      setCommentToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCommentToDelete(null);
  };

  const loadMore = () => {
    setDisplayCount(prev => prev + COMMENTS_PER_PAGE);
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${hours}:${minutes} ${month} ${day} ${year}`;
  };

  const displayedComments = comments.slice(0, displayCount);
  const hasMore = comments.length > displayCount;

  if (loading) {
    return (
      <div className="guestbook">
        <div className="guestbook-loading">Loading guestbook...</div>
      </div>
    );
  }

  return (
    <div className="guestbook">
      {/* Login/Signup Prompt or Comment Input */}
      {showLoginPrompt ? (
        <div className="guestbook-welcome-container">
          <h2 className="guestbook-welcome-header">Welcome to guestBook</h2>
          <p className="guestbook-welcome-text">
            <span
              className="guestbook-welcome-link"
              onClick={() => onOpenLoginSignup && onOpenLoginSignup()}
            >
              login or signup
            </span> to leave a note
          </p>
        </div>
      ) : (
        <div className="guestbook-input-container">
          <div className="guestbook-textarea-wrapper">
            <textarea
              className="guestbook-textarea"
              placeholder="type to leave a note..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={MAX_CHAR_LIMIT}
              disabled={posting}
            />
          </div>
          {selectedPhoto && (
            <div className="guestbook-photo-preview">
              <img src={selectedPhoto} alt="Selected" />
              <button
                className="guestbook-photo-remove"
                onClick={() => setSelectedPhoto(null)}
                title="Remove photo"
              >
                ×
              </button>
            </div>
          )}
          {selectedGardenPageId && userHandle && (
            <div className="guestbook-garden-preview">
              <iframe
                src={`https://mindgarden.page/${userHandle}/${selectedGardenPageId}`}
                title="Garden Preview"
                style={{
                  width: '100%',
                  height: '300px',
                  border: '2px solid #007AFF',
                  borderRadius: '4px'
                }}
              />
              <button
                className="guestbook-photo-remove"
                onClick={() => setSelectedGardenPageId(null)}
                title="Remove garden"
              >
                ×
              </button>
            </div>
          )}
          <div className="guestbook-input-footer">
            <div className="guestbook-input-left">
              <button
                className="guestbook-attach-button"
                onClick={() => setShowPhotoSelector(true)}
                disabled={posting}
                title="Attach photo from PhotoBooth"
              >
                <img src={photoboothIcon} alt="PhotoBooth" className="guestbook-attach-icon" />
                <span>attach photo</span>
              </button>

              {userGardenPageId && (
                <button
                  className="guestbook-attach-button"
                  onClick={() => {
                    setSelectedGardenPageId(userGardenPageId);
                  }}
                  disabled={posting}
                  title="Attach your Garden page"
                >
                  <img src={gardenIcon} alt="Garden" className="guestbook-attach-icon" />
                  <span>attach garden</span>
                </button>
              )}
            </div>
            <div className="guestbook-input-right">
              <span className="guestbook-char-count">
                {message.length}/{MAX_CHAR_LIMIT}
              </span>
              <button
                className="guestbook-post-button"
                onClick={handlePost}
                disabled={posting || !message.trim() || message.length > MAX_CHAR_LIMIT}
              >
                {posting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments List */}
      <div className="guestbook-comments-wrapper" ref={scrollContainerRef}>
        <div className="guestbook-comments">
        {displayedComments.length === 0 ? (
          <div className="guestbook-empty">No notes yet. Be the first to leave one!</div>
        ) : (
          <>
            {displayedComments.map((comment) => (
              <div key={comment.id} className="guestbook-comment">
                <div className="guestbook-comment-header">
                  <div className="guestbook-comment-header-left">
                    <span
                      className="guestbook-comment-author"
                      style={{ color: getColorForHandle(comment.handle) }}
                    >
                      @{comment.handle}
                    </span>
                  </div>
                  <span className="guestbook-comment-timestamp">
                    {formatTimestamp(comment.timestamp)}
                  </span>
                  {currentUser && currentUser.uid === comment.userId && (
                    <button
                      className="guestbook-comment-delete"
                      onClick={() => handleDeleteClick(comment.id)}
                      title="Delete comment"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="guestbook-comment-message">{comment.message}</div>
                {comment.photoUrl && (
                  <div
                    className="guestbook-comment-photo"
                    onClick={() => onOpenImage && onOpenImage(comment.photoUrl!, `@${comment.handle}`)}
                  >
                    <img src={comment.photoUrl} alt="Comment photo" />
                  </div>
                )}
                {comment.gardenPageId && (
                  <div
                    className="guestbook-comment-garden"
                    onClick={() => onOpenGarden && onOpenGarden(comment.gardenPageId!, comment.handle)}
                    style={{
                      width: '270px',
                      height: '202.5px',
                      overflow: 'hidden'
                    }}
                  >
                    <iframe
                      src={`https://mindgarden.page/${comment.handle}/${comment.gardenPageId}`}
                      title={`@${comment.handle}'s garden`}
                      style={{
                        width: '480px',
                        height: '360px',
                        border: 'none',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        transform: 'scale(0.5625)',
                        transformOrigin: 'top left'
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
            {hasMore && (
              <button className="guestbook-load-more" onClick={loadMore}>
                Load More
              </button>
            )}
          </>
        )}
        </div>
      </div>

      {/* Photo Selector Modal */}
      {showPhotoSelector && (
        <div className="guestbook-modal-overlay" onClick={() => setShowPhotoSelector(false)}>
          <div className="guestbook-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="guestbook-modal-title">Select a Photo</h3>
            {photoboothPhotos.length === 0 ? (
              <div className="guestbook-modal-empty">
                <p className="guestbook-modal-text">
                  No photos found. Snap a photo in photobooth and they'll show up here!
                </p>
                <button
                  className="guestbook-snap-photo-button"
                  onClick={() => {
                    setShowPhotoSelector(false);
                    onOpenPhotoBooth?.();
                  }}
                >
                  <img src={photoboothIcon} alt="Photo Booth" className="guestbook-snap-icon" />
                  <span>Open Photo Booth</span>
                </button>
              </div>
            ) : (
              <div className="guestbook-photo-grid">
                {photoboothPhotos.map((photo) => (
                  <div
                    key={photo.id}
                    className="guestbook-photo-option"
                    onClick={() => {
                      setSelectedPhoto(photo.dataUrl);
                      setShowPhotoSelector(false);
                    }}
                  >
                    <img src={photo.dataUrl} alt={`Photo ${photo.id}`} />
                  </div>
                ))}
              </div>
            )}
            <div className="guestbook-modal-buttons">
              <button
                className="guestbook-modal-cancel"
                onClick={() => setShowPhotoSelector(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="guestbook-modal-overlay" onClick={cancelDelete}>
          <div className="guestbook-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="guestbook-modal-title">Delete Comment</h3>
            <p className="guestbook-modal-text">
              Are you sure you want to delete this comment? This action cannot be undone.
            </p>
            <div className="guestbook-modal-buttons">
              <button className="guestbook-modal-cancel" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="guestbook-modal-confirm" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Guestbook;
